import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import { RateFormStyled } from "./rate-card-form.styles";
import TextInputField from "../../../../components/form-fields/text-input-field/text-input-form-field";
import SelectField from "../../../../components/form-fields/select-field/select-field";
import { Checkbox, FormControlLabel } from "@mui/material";
import { Typography } from "@mui/material";

const RateCardForm = ({ form }) => {
  const { control, getValues, watch, setValue } = form;
  const formData = getValues();

  // Now stored in form state instead of local component state
  const rateOption = watch("rateOption");
  const flatRateValue = watch("flatRate");

  // Whenever flatRate changes and we're in flat mode, propagate to all roles
  useEffect(() => {
    if (rateOption === "flat" && flatRateValue !== undefined) {
      formData.serviceGroups.forEach((group, gi) => {
        group.roles.forEach((role, ri) => {
          setValue(`serviceGroups.${gi}.roles.${ri}.rate`, flatRateValue);
        });
      });
    }
  }, [flatRateValue, rateOption, formData.serviceGroups, setValue]);

  return (
    <RateFormStyled>
      {/* Rate Card Name */}
      <Controller
        name="name"
        control={control}
        defaultValue=""
        rules={{ required: "Rate card name is required" }}
        render={({ field, fieldState }) => (
          <TextInputField
            {...field}
            isRequired
            label="Rate Card Name"
            placeholder="Ex. Default Agency Rate Card"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      {/* Toggle Per Role / Flat Rate */}
      <div className={rateOption !== "flat" ? "role-option" : "flat-option"}>
        <div className="rate">
          <Typography variant="body5">Rate(s):</Typography>
          <Controller
            name="rateOption"
            control={control}
            defaultValue=""
            render={({ field, fieldState }) => (
              <SelectField
                {...field}
                isRequired
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                options={[
                  { label: "Per Role", value: "role" },
                  { label: "Flat Rate", value: "flat" },
                ]}
              />
            )}
          />
        </div>

        {rateOption === "flat" && (
          <Controller
            name="flatRate"
            control={control}
            defaultValue=""
            rules={{ required: "Flat rate is required" }}
            render={({ field, fieldState }) => (
              <TextInputField
                {...field}
                inputType="number"
                placeholder="0.00"
                InputProps={{ startAdornment: <div>$</div> }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ width: "100px" }}
              />
            )}
          />
        )}
        {/* Only show default toggle when not in flat mode */}
        {rateOption !== "flat" && (
          <div className="default-rate">
            <Typography variant="body5">Set as default</Typography>
            <Controller
              name="isDefault"
              control={control}
              defaultValue={formData.isDefault}
              render={({ field }) => {
                const handleToggle = (e) => {
                  const willSetDefault = e.target.checked;
                  if (willSetDefault) {
                    const confirmed = window.confirm(
                      "Setting this rate card as the default will establish the standard system rates for all roles and update any clients currently using the default rate card for the company. If a different rate card is already set as the default, it will be replaced by this one."
                    );
                    if (!confirmed) {
                      // user clicked “Cancel” → don’t update the value
                      return;
                    }
                  }
                  // either they unchecked, or they confirmed → update form state
                  field.onChange(willSetDefault);
                };

                return (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        sx={{
                          color: "#080425",
                          "&.Mui-checked": {
                            color: "#080425",
                          },
                        }}
                        checked={field.value}
                        onChange={handleToggle}
                      />
                    }
                  />
                );
              }}
            />
          </div>
        )}
      </div>
      {/* Per-role rate inputs (disabled in flat mode) */}
      {formData.serviceGroups?.map((group, gi) => (
        <div key={group.id} className="services">
          <Typography variant="body6">{group.name}</Typography>
          <div className="roles">
            {group.roles.map((role, ri) => (
              <div key={role.id} className="role">
                <Typography variant="body1">{role.name}</Typography>
                <Controller
                  name={`serviceGroups.${gi}.roles.${ri}.rate`}
                  control={control}
                  defaultValue={role.rate || ""}
                  rules={{
                    required: "Rate is required",
                    validate: (value) =>
                      parseFloat(value) > 1 || "Rate must be greater than 1",
                  }}
                  render={({ field, fieldState }) => (
                    <TextInputField
                      {...field}
                      inputType="number"
                      placeholder="0.00"
                      InputProps={{ startAdornment: <div>$</div> }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      sx={{ width: "100px" }}
                      disabled={rateOption === "flat"}
                    />
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </RateFormStyled>
  );
};

export default RateCardForm;
